import {
  useQuery,
  useQueryClient,
  useMutation,
  UseQueryResult,
} from 'react-query';
import sortBy from 'lodash/fp/sortBy';
import path from 'lodash/fp/path';
import get from 'lodash/get'
import * as Map from '../util/Map';
import * as api from '../util/api';
import { UserSettings, Application, Workflow, CNumber, DMSBoxMapping, UserAppViewSettings, IAdoptEaseUsage, UserLocale } from '../types';
import useAuthToken, { getAuthToken } from './useAuthToken';
import { UserDetailsResponse } from '../util/Interfaces/IUserDetailsResponse';
import { UserInfo } from './../types';
import { INotification } from '../components/Notifications/Notification';
import { useNotificationStore } from '../components/Notifications/NotificationStore';
import { useApplicationStore, useWorkflowsStore } from '../Store';
import { UserCard } from '../components/UserCards/UserCards';
import { detectBrowser } from '../util/DriveUtils';
import { useVanityDomainStatusStore } from '../Store/useVanityDomainStatusStore';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk'
import { useFeatureFlagWithFailsafe } from './useFeatureFlagWithFailsafe'
import { UniPulseUrlParams } from '../components/UniPulse/UserLookForm';
import { AxiosError } from 'axios';
import { updateDefaultApplication, updateDefaultWorkflow } from '../util/api';
const sortByName = sortBy(path('name'));

interface ErrorObject {
  status?: string;
  message?: string;
  error?: string;
}

export function useWorkflowsViewMap(onlyFavorites?: boolean) {
  const token = useAuthToken();
  const oktaUser = useUserProfile();
  const oktaUserInfo = (oktaUser.data || {}) as UserInfo;
  const locale = get(oktaUserInfo, "locale") as 'en_US' | 'fr_CA'
  const enterpriseId = get(oktaUserInfo, "userProfile.enterpriseId", "")
  const isSessionAvailableOnVanity = useVanityDomainStatusStore((state: any) => state.isSessionAvailableOnVanity);
  const { data = [], status } = useQuery(
    'workflows',
    () => api.fetchWorkflows(getAuthToken(), enterpriseId, isSessionAvailableOnVanity),
    {
      select: (data) =>
        sortByName(
          data?.filter((workflow) =>
            onlyFavorites ? workflow.isFavorite : true
          )
        ),
      enabled: !!token && !!locale
    }
  );
  return { data: Map.workflowListToWorkflowViewMap(data), status };
}

export function useGetAllApplications() {
  const token = useAuthToken();
  const {
    data = [],
  } = useQuery('allApplications', () => api.fetchAllApplications(token), {
    select: sortByName,
    enabled: !!token,
  });
  return {
    data
  };
};

export function useGetAllWorkflows() {
  const token = useAuthToken();
  const {
    data = [],
  } = useQuery('allWorkflows', () => api.fetchAllWorkflows(token), {
    select: sortByName,
    enabled: !!token,
  });
  return {
    data
  };
};

export function useApplications() {
  const token = useAuthToken();
  const oktaUser = useUserProfile();
  const oktaUserInfo = (oktaUser.data || {}) as UserInfo;
  const locale = get(oktaUserInfo, "locale") as unknown as 'en_US' | 'fr_CA'
  const enterpriseId = get(oktaUserInfo, "userProfile.enterpriseId", "")
  const isSessionAvailableOnVanity = useVanityDomainStatusStore((state: any) => state.isSessionAvailableOnVanity);

  const {
    data = [],
    status,
    isFetched,
  } = useQuery('applications', () => api.fetchApplications(getAuthToken(), enterpriseId, isSessionAvailableOnVanity), {
    select: sortByName,
    enabled: !!token && !!locale,
  });

  let apiStatus = status;
  if (
    data &&
    data.length > 0 &&
    data[0] &&
    Object.keys(data[0]).includes('error')
  ) {
    //This condition is considered as the Success state and handling it in ApplicationList.tsx and WorkflowsTab.tsx
    if (
      oktaUserInfo &&
      oktaUserInfo.userProfile &&
      Object.keys(oktaUserInfo?.userProfile as object).includes('login') &&
      data[0].error.includes(
        `User ${(oktaUserInfo.userProfile as any).login} does not exist`
      )
    ) {
      data.splice(0, data.length);
    } else {
      apiStatus = 'error';
    }
  }
  return {
    data,
    apiStatus,
    isFetched,
  };
}

export function useWorkflows() {
  const token = useAuthToken();
  const oktaUser = useUserProfile();
  const oktaUserInfo = (oktaUser.data || {}) as UserInfo;
  const enterpriseId = get(oktaUserInfo, "userProfile.enterpriseId", "")
  const locale = get(oktaUserInfo, "locale") as 'en_US' | 'fr_CA'
  const isSessionAvailableOnVanity = useVanityDomainStatusStore((state: any) => state.isSessionAvailableOnVanity);
  const { data, status, isFetched } = useQuery('workflows', () => api.fetchWorkflows(getAuthToken(), enterpriseId, isSessionAvailableOnVanity), {
    enabled: !!token && !!locale,
  });

  return { data, status, isFetched };
}

export function useAllWorkflowsAndApplications() {
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const applications = useApplicationStore((state: any) => state.applications);

  return {
    data: [
      ...workflows.map(Map.workflowOrApplicationToPaneData),
      ...applications.map(Map.workflowOrApplicationToPaneData),
    ],
  };
}

export function useAllWorkflowsAndApplicationsById(ids: string[]) {
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const applications = useApplicationStore((state: any) => state.applications);

  const result = [...ids].reverse().map((id) => {
    const workflow = workflows.find((item: any) => item.id === id);
    if (workflow) {
      return Map.workflowOrApplicationToPaneData(workflow);
    }
    const application = applications.find((item: any) => item.id === id);
    if (application) {
      return Map.workflowOrApplicationToPaneData(application);
    }
    return null;
  });
  return result.filter((item) => item !== null) as (Workflow | Application)[];
}

export const useUpdateDefaultApplication = () => {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ id, isDefault }: { id: string; isDefault: boolean }) =>
      updateDefaultApplication(id, isDefault, token),
    onSuccess: () => {
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['unifyProfile']);
    },
  });
};

export const useUpdateDefaultWorkflow = () => {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  return useMutation({
    mutationFn: ({ id, isDefault }: { id: string; isDefault: boolean }) =>
      updateDefaultWorkflow(id, isDefault, token),
    onSuccess: () => {
      queryClient.invalidateQueries(['applications']);
      queryClient.invalidateQueries(['unifyProfile']);
      queryClient.invalidateQueries(['workflows']);
    },
  });
};

export function useUpdateApplication() {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { data, mutate } = useMutation<
    string,
    unknown,
    { id: string; isFavorite: boolean },
    { cachedList: Application[] }
  >(
    ({ id, isFavorite }: { id: string; isFavorite: boolean }) =>
      api.updateApplications(id, isFavorite, token),
    {
      onMutate: async ({ id }: { id: string }) => {
        await queryClient.cancelQueries('applications');
        const cachedList =
          (queryClient.getQueryData('applications') as Application[]) || [];

        queryClient.setQueryData(
          'applications',
          (oldList: Application[] = []) => {
            return oldList.map((app) => {
              return app.id === id
                ? { ...app, isFavorite: !app.isFavorite }
                : app;
            });
          }
        );

        return { cachedList };
      },

      onError: (_error, _data, context) => {
        queryClient.setQueryData('applications', context?.cachedList);
      },
    }
  );

  return { data, mutate };
}

export function useUpdateWorkflow() {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { data, mutate } = useMutation<
    string,
    unknown,
    { id: string; isFavorite: boolean; appId: string },
    { cachedList: Workflow[] }
  >(
    ({ id, isFavorite, appId }) =>
      api.updateWorkflows(id, appId, isFavorite, token),
    {
      onMutate: async ({ id }) => {
        await queryClient.cancelQueries('workflows');
        const cachedList =
          (queryClient.getQueryData('workflows') as Workflow[]) || [];
        queryClient.setQueryData('workflows', (oldList: Workflow[] = []) => {
          return oldList.map((app) => {
            return app.id === id
              ? { ...app, isFavorite: !app.isFavorite }
              : app;
          });
        });

        return { cachedList };
      },

      onError: (_error, _data, context) => {
        queryClient.setQueryData('workflows', context?.cachedList);
      },
    }
  );

  return { data, mutate };
}

export function useUserProfile() {
  const token = useAuthToken();
  const setIsSessionAvailableOnVanity = useVanityDomainStatusStore((state: any) => state.setIsSessionAvailableOnVanity);
  const vanityDomainFlag = false; //useFeatureFlag('UNIFY_UI_VANITY');
  const enableIdpProxyFlag = useFeatureFlagWithFailsafe('UNIFY_ENABLE_IDP_PROXY');

  const {
    data = null,
    isLoading = true,
    isError = false,
    refetch,
    status,
  } = useQuery(
    '/profile',
    () => api.fetchProfile(
      setIsSessionAvailableOnVanity,
      vanityDomainFlag,
      getAuthToken(),
      enableIdpProxyFlag
    ),
    { enabled: !!token && vanityDomainFlag !== undefined && enableIdpProxyFlag !== undefined, retry: 3 }
  );

  return { data, isLoading, isError, refetch, status };
}

export function useApplicationCategory() {
  const token = useAuthToken();
  const { data } = useQuery('applicationCategories', () => api.fetchApplicationCategory(token), {
    enabled: !!token,
  });
  return { data };
}

export function useIAMUserProfile() {
  const token = useAuthToken();
  const {
    data = null,
    isLoading = true,
    isError = false,
  } = useQuery('/iam-profile', () => api.fetchIAMProfile(token), {
    retry: false,
    enabled: !!token,
  });

  return { data, isLoading, isError };
}

export function useUserSettings() {
  const token = useAuthToken();
  const { data = null, status } = useQuery(
    'settings',
    () => api.fetchSettings(getAuthToken()),
    { enabled: !!token }
  );

  return { data, status };
}

export function useUnifyUserProfile() {
  const token = useAuthToken();
  const { data = null, status } = useQuery(
    'unifyProfile',
    () => api.fetchUnifyProfile(getAuthToken()),
    { enabled: !!token }
  );

  return { data, status };
}

export function useUpdateUserSettings() {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (settings: UserSettings) => api.updateSettings(settings, token),
    {
      onMutate: async (settings: UserSettings) => {
        await queryClient.cancelQueries('settings');
        const cachedSettings = queryClient.getQueryData(
          'settings'
        ) as UserSettings;
        queryClient.setQueryData('settings', settings);
        return { cachedSettings };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('settings', context?.cachedSettings);
      },
      onSettled: () => {
        queryClient.invalidateQueries('settings');
      },
    }
  );
  return { data, mutate };
}

export function useUpdateUserAppViewSettings() {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (settings: UserAppViewSettings) => api.updateAppViewSettings(settings, token),
    {
      onMutate: async (settings: UserAppViewSettings) => {
        await queryClient.cancelQueries('settings');
        const cachedSettings = queryClient.getQueryData(
          'settings'
        ) as UserAppViewSettings;
        queryClient.setQueryData('settings', settings);
        return { cachedSettings };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('settings', context?.cachedSettings);
      },
      onSettled: () => {
        queryClient.invalidateQueries('settings');
      },
    }
  );
  return { data, mutate };
}

export function useUpdateUserLocaleSettings() {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (locale: UserLocale) => api.updateUserLocaleSettings(locale, token),
    {
      onMutate: async (locale: UserLocale) => {
        await queryClient.cancelQueries('userlocale');
        const cachedSettings = queryClient.getQueryData(
          'userlocale'
        ) as UserLocale;
        queryClient.setQueryData('userlocale', locale);
        return { cachedSettings };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('userlocale', context?.cachedSettings);
      },
      onSettled: async() => {
        queryClient.invalidateQueries('userlocale');
        await queryClient.invalidateQueries('/profile');
        await new Promise(resolve => setTimeout(resolve, 1000));
        queryClient.invalidateQueries('campaigns');
        queryClient.invalidateQueries('applications');
        queryClient.invalidateQueries('workflows');
        queryClient.invalidateQueries('notification');
      },
    }
  );
  return { data, mutate };
}

export function useBanner() {
  const token = useAuthToken();
  const {
    data = null,
    isLoading = true,
    isError = false,
  } = useQuery('/getBanner', () => api.fetchBanner(token), {
    enabled: !!token,
  });

  return { data, isLoading, isError };
}

export const useFetchDMS = (isDriveUser: boolean): UseQueryResult<CNumber[]> => {
  const authToken = useAuthToken();
  // @Ashif - should implement persistent cache on queryClient with cacheTime? I reckon!
  return useQuery(['iam/fetchCNumbers', isDriveUser], () => api.fetchCNumbers(authToken), {
    enabled: !!authToken && isDriveUser === true,
  });
};
export const useFetchDMSBoxMapping = (isDriveUser: boolean): UseQueryResult<DMSBoxMapping> => {
  const authToken = useAuthToken();
  const enterpriseId = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')
    .accessToken
    ?.claims
    ?.enterpriseId
  return useQuery('iam/fetchDMSBoxMapping', () => api.fetchDMSBoxMapping(authToken, enterpriseId), {
    enabled: !!authToken && isDriveUser === true,
  });
};

/**
 * Common BFF User Queries
 */

export const useFetchEnterpriseUserDetails =
  (): UseQueryResult<UserDetailsResponse> => {
    const authToken = useAuthToken();

    return useQuery(
      'commonBff/user/details',
      () => api.fetchEnterpriseUserDetails(authToken),
      {
        enabled: !!authToken,
      }
    );
  };

/**
 * Notification Queries
 */

export function useNotifications() {
  const token = useAuthToken();
  const { data } = useQuery('notification', () => api.fetchNotifications(token), {
    enabled: !!token,
  });
  return { data };
}

export function useUpdateUserLastLogin() {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const { mutate } = useMutation(
    (data: { lastLogin: any, enterpriseId: any, storeId: string, permissions:string[] }) =>
      api.updateUserLastLogin(data.lastLogin, data.enterpriseId, data.storeId, data.permissions, token), {
    onSettled: () => {
      queryClient.invalidateQueries('unifyProfile');
    }
  }
  );
  return { mutate };
}

export function useUpdateUserCNumber() {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const { mutate } = useMutation(
    (data: { cNumbers: any }) => api.updateUserCNumber(data.cNumbers, token), {
    onSettled: () => {
      queryClient.invalidateQueries('unifyProfile');
    }
  }
  );
  return { mutate };
}

export function useUpdateNotification() {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    ({ notification, read = true }: { notification: INotification; read?: boolean }) =>
      api.updateUserReadMessage(notification.messageId!, token, read),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['notification'] });
        const cachedNotification = queryClient.getQueryData(
          'notification'
        ) as INotification;
        queryClient.setQueryData('notification', cachedNotification);
        return { cachedNotification };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('notification', context?.cachedNotification);
      },
      onSettled: () => {
        queryClient.invalidateQueries('notification');
      },
    }
  );
  return { data, mutate };
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const readAll = useNotificationStore((state: any) => state.readAll);

  const { data, mutate: mutateAllRead } = useMutation(
    (messageIds: string[]) => api.updateAllUserReadMessages(messageIds, token),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['notification'] });
        const cachedNotification = queryClient.getQueryData(
          'notification'
        ) as INotification;
        readAll();
        return { cachedNotification };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('notification', context?.cachedNotification);
      },
      onSettled: () => {
        queryClient.invalidateQueries('notification');
      },
    }
  );

  // Snapshot the currently-unread ids before onMutate's optimistic readAll()
  // flips everything to read, otherwise the mutation would always send [].
  const mutate = () => {
    const unreadIds = useNotificationStore
      .getState()
      .notifications.filter((n: INotification) => !n.read && !n.deleted)
      .map((n: INotification) => n.messageId!);
    mutateAllRead(unreadIds);
  };

  return { data, mutate };
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (notification: INotification) => api.deleteUserMessage(notification.messageId!, token),
    {
      onMutate: async (notification: INotification) => {
        await queryClient.cancelQueries({ queryKey: ['notification'] });
        const cachedNotification = queryClient.getQueryData(
          'notification'
        ) as INotification;
        queryClient.setQueryData('notification', cachedNotification);
        return { cachedNotification };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('notification', context?.cachedNotification);
      },
      onSettled: () => {
        queryClient.invalidateQueries('notification');
      },
    }
  );
  return { data, mutate };
}

export function useDeleteAllNotification() {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const removeAll = useNotificationStore((state: any) => state.removeAll);
  const setNotifications = useNotificationStore((state: any) => state.setNotifications);

  const { data, mutate } = useMutation(
    (notifications: string[]) => api.deleteAllUserMessages(notifications, token),
    {
      onMutate: async (messageIds: string[]) => {
        await queryClient.cancelQueries({ queryKey: ['notification'] });
        const cachedNotification = queryClient.getQueryData(
          'notification'
        ) as INotification;
        const previousNotifications = useNotificationStore.getState().notifications;
        removeAll(messageIds);
        return { cachedNotification, previousNotifications };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('notification', context?.cachedNotification);
        if (context?.previousNotifications) {
          setNotifications(context.previousNotifications);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries('notification');
      },
    }
  );
  return { data, mutate };
}

export function useCampaigns() {
  const { data: oktaUser } = useUserProfile();
  const oktaUserInfo = (oktaUser || {}) as UserInfo;
  const locale = get(oktaUserInfo, "locale") ?? '';

  const applicationLoadStatus = useApplicationStore((state: any) => state.apiStatus);
  const applications = useApplicationStore((state: any) => state.applications);
  const workflowLoadStatus = useWorkflowsStore((state: any) => state.apiStatus);
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const { data: userProfile, isLoading } = useIAMUserProfile();

  const token = useAuthToken();
  const { data = null, status } = useQuery(
    'campaigns',
    () => api.fetchUnifyCampaigns(getAuthToken(), applications?.map((app: any) => app.id) || [],
    workflows?.map((workflow:any) => workflow.id) || [], userProfile?.fullUser?.enterprise?.id || '' , 
    userProfile?.fullUser?.stores.map((store: any ) => store.id) || []),
    { enabled: !!token && !!locale && (applicationLoadStatus === 'success' || applicationLoadStatus === 'error')
      && (workflowLoadStatus === 'success' || workflowLoadStatus === 'error')
      && !isLoading
    }
  );

  return { data, status };
}

export function useUserCards(enterpriseId: string) {
  const linkFlag = useFeatureFlag('DEALER_UI_LINKS');
  const token = useAuthToken();
  const { data } = useQuery('userCards', () => api.fetchUserCards(token, enterpriseId), {
    enabled: !!token && linkFlag && !!enterpriseId,
  });
  return { data };
}

export function useDeleteCustomAppCard({onSuccess, onError}: any) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (userCard: UserCard) => api.deleteUserCard(userCard, token),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['deleteUserCard'] });
        const cachedUserCard = queryClient.getQueryData(
          'deleteUserCard'
        ) as UserCard;
        queryClient.setQueryData('deleteUserCard', cachedUserCard);
        return { cachedUserCard };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('deleteUserCard', context?.cachedUserCard);
      },
      onSettled: () => {
        queryClient.invalidateQueries('userCards');
      },
    }
  );
  return { data, mutate };
}

export function useCreateUserLink({onSuccess, onError}: any) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (userCard: UserCard) => api.createUserCard(userCard, token),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['createUserCard'] });
        const cachedUserCard = queryClient.getQueryData(
          'createUserCard'
        ) as UserCard;
        queryClient.setQueryData('createUserCard', cachedUserCard);
        return { cachedUserCard };
      },
      onError: (_error, _data, context) => {
        const errorTyp = _error as AxiosError<ErrorObject>;
        onError(errorTyp?.response?.data.error);
        queryClient.setQueryData('createUserCard', context?.cachedUserCard);
      },
      onSuccess: (_data) => {
        onSuccess();
        queryClient.invalidateQueries('userCards');
      },
    }
  );
  return { data, mutate };
}

export function useUpdateUserLink({onSuccess, onError}: any) {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (userCard: UserCard) => api.updateUserCard(userCard, token),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['updateUserCard'] });
        const cachedUserCard = queryClient.getQueryData(
          'updateUserCard'
        ) as UserCard;
        queryClient.setQueryData('updateUserCard', cachedUserCard);
        return { cachedUserCard };
      },
      onError: (_error, _data, context) => {
        onError();
        queryClient.setQueryData('updateUserCard', context?.cachedUserCard);
      },
      onSettled: () => {
        onSuccess();
        queryClient.invalidateQueries('userCards');
      },
    }
  );
  return { data, mutate };
}

export function useDepartmentList() {
  const token = useAuthToken();

  const { data } = useQuery('departmentsList', () => api.fetchDepartments(token), {
    enabled: !!token
  });

  return { data };
}

export function useEncryptParams() {
  const token = useAuthToken();
  const { data } = useQuery('encryptParams', () => api.fetchEncryptParams(token, detectBrowser()), {
    enabled: !!token,
  });
  return { data };
}

export function useCspUrls() {
  const token = useAuthToken();
  const { data } = useQuery('cspurls', () => api.fetchCspUrls(token), {
    enabled: !!token,
  });
  return { data };
}

/**
 * AdoptEase calls Start
 */

export function useAdoptEaseConfig() {
  const adoptEaseDriveFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE');
  const adoptEaseApplicationFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_APPLICATION');
  const adoptEaseJournalEntryFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_JOURNALE');
  const adoptEaseCustomerFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_CUSTOMER');
  const adoptEaseVendorFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_VENDOR');

  const adoptEaseFlag = adoptEaseDriveFlag || adoptEaseApplicationFlag || adoptEaseJournalEntryFlag || adoptEaseCustomerFlag || adoptEaseVendorFlag;

  const token = useAuthToken();

  const { data, status, isFetched } = useQuery('adoptEaseConfig', () => api.fetchAdoptEaseConfig(token, adoptEaseFlag), {
    enabled: !!token && adoptEaseFlag !== undefined,
  });
  return { data, status, isFetched };

}

export function useAdoptEaseUsageByUser() {
  const adoptEaseDriveFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE');
  const adoptEaseJournalEntryFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_JOURNALE');
  const adoptEaseCustomerFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_CUSTOMER');
  const adoptEaseVendorFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_VENDOR');
  const adoptEaseFlag = adoptEaseDriveFlag || adoptEaseJournalEntryFlag || adoptEaseCustomerFlag || adoptEaseVendorFlag;
  const token = useAuthToken();
  const {data, status, isFetched } = useQuery('adoptEaseUsageByUser', () => api.fetchAdoptEaseUsageByUser(token, adoptEaseFlag), {
    enabled: !!token && adoptEaseFlag !== undefined,
  });
  return { data, status, isFetched };
}

export function useUpdateUserAdoptEaseUsage() {
  const queryClient = useQueryClient();
  const token = useAuthToken();

  const { data, mutate } = useMutation(
    (workflowDetails: Record<string, Date>) => api.updateUserAdoptEaseUsage(token, workflowDetails),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['adoptEaseUsageByUser'] });
        const adoptEaseUsage = queryClient.getQueryData(
          'adoptEaseUsageByUser'
        ) as IAdoptEaseUsage[];
        queryClient.setQueryData('adoptEaseUsageByUser', adoptEaseUsage);
        return { adoptEaseUsage };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('adoptEaseUsageByUser', context?.adoptEaseUsage);
      },
      onSettled: () => {
        queryClient.invalidateQueries('adoptEaseUsageByUser');
      },
    }
  );
  return { data, mutate };
}

/**
 * AdoptEase calls End
 */

export function useSystemHealthCards() {
  const token = useAuthToken();
  const { data } = useQuery('useSystemHealthCards', () => api.fetchSystemHealthCards(token), {
    enabled: !!token,
  });
  return { data };
}

export function useFetchActivityUsers() {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  let queryKey = 'fetchActivitySearchAndGroupByUser';
  const { data, mutate } = useMutation(
    (urlParams: UniPulseUrlParams) => {
      const { startDate, endDate, storeids, loginid, ip, enterpriseId, groupType } = urlParams;

      return api.fetchActivitySearchAndGroupByUser(token, startDate, endDate, storeids, loginid, ip, enterpriseId, groupType)
    },
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['useFetchActivityUsers'] });
        const cachedPulseUsers = queryClient.getQueryData(
          queryKey
        ) as any;
        queryClient.setQueryData('useFetchActivityUsers', cachedPulseUsers);
        return { cachedPulseUsers };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('useFetchActivityUsers', context?.cachedPulseUsers);
      },
      onSettled: () => {
        queryClient.invalidateQueries('useFetchActivityUsers');
      },
    }
  );
  return { data, mutate };
}

export function useCMFNumber() {
  const token = useAuthToken();
  const { data, mutate, status } = useMutation(
    (data: { storeId: string, enterpriseId: string }) =>
      api.fetchCMFNumber(token, data.enterpriseId, data.storeId)
  );
  return { data, mutate, status };
}

export function useGetAllMessagingApps() {
  const token = useAuthToken();
  const {
    data = [],
  } = useQuery('allMessagingApps', () => api.fetchAllMessagingApps(token), {
    select: sortByName,
    enabled: !!token,
  });
  return {
    data
  };
};

export function useGetAhaPilotConfig() {
  const token = useAuthToken();
  const {
    data = {} as any,
  } = useQuery('ahaPilotConfig', () => api.fetchAhaPilotConfig(token), {
    select: sortByName,
    enabled: !!token,
  });
  return {
    data
  };
};
